import * as React from "react";
import { Loader2, Search, X } from "lucide-react";
import { cn } from "../lib/utils";

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value"> {
  value?: string;
  onValueChange?: (value: string) => void;
  /** Show a spinner in the addon slot (async searching). */
  loading?: boolean;
  /** Shortcut hint shown on the right, e.g. ["⌘", "K"]. */
  shortcut?: [string, string];
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, value, onValueChange, loading = false, shortcut, onChange, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
      if (typeof ref === "function") ref(innerRef.current);
      else if (ref) ref.current = innerRef.current;
    });

    return (
      <div className={cn("relative flex items-center", className)}>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 size-4 text-muted-foreground"
        />
        <input
          ref={innerRef}
          type="search"
          value={value}
          role="searchbox"
          onChange={(e) => {
            onChange?.(e);
            onValueChange?.(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape" && value) onValueChange?.("");
          }}
          className={cn(
            "h-9 w-full rounded-lg border border-input bg-background pl-9 pr-16 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            "[&::-webkit-search-cancel-button]:appearance-none",
          )}
          {...props}
        />
        <span className="absolute right-2 flex items-center gap-1">
          {loading ? (
            <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden="true" />
          ) : value ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onValueChange?.("")}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          ) : shortcut ? (
            <span aria-hidden="true" className="flex items-center gap-0.5">
              {shortcut.map((k) => (
                <kbd
                  key={k}
                  className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md border border-border bg-secondary px-1.5 font-mono text-[11px] text-secondary-foreground"
                >
                  {k}
                </kbd>
              ))}
            </span>
          ) : null}
        </span>
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
